import {
  DAMPING_DOWN_DEFAULT,
  DAMPING_UP_DEFAULT,
  FORECAST_GROWTH_DEFAULT,
  STANDARD_DEVIATION_MULTIPLIER_DEFAULT,
  STANDARD_DEVIATION_TERM_DEFAULT
} from '@components/stable-balance/stable-balance-volatility-adjustments/default-values';
import { ICalibration } from '@models/calibrations/calibration';
import { ICalibrationChangelog } from '@models/calibrations/calibration-changelog';
import { IForecastSettings } from './forecast-settings';
import { IHistoricalSettings } from './historical-settings';
import { PolynomialForms } from './polynomial-forms';
import { IBalanceSeries } from './unadjusted-balance';

export interface IStableBalanceCalibration extends ICalibration {
  CalibrationId?: number;
  CalibrationStartDate: string;
  CalibrationEndDate: string;
  ForecastEndDate: string;
  ForecastStartDate: string;
  LastObservationDate: string;

  /**
   * Include all estimate's **regression (beta) coefficients**, last item of each row correspond to the estimate's **RSquared** value.
   */
  Coefficients: number[][];
  DateTimeCreated?: string;
  DateTimeModified?: string;
  SavedDate?: string;
  SelectedAcquisitionIds: number[];
  SelectedPolynomialForm: PolynomialForms;

  Adjustments: IStableBalanceSeries[];
  UnadjustedBalances?: IStableBalanceSeries[];
  TotalBalances?: IStableBalanceSeries[];
  FittedBalances?: IStableBalanceSeries[];
  VolatilityAdjustments?: IStableBalanceSeries[];
  GrowthRates?: IStableBalanceSeries[];
  ConstrainedRates?: IStableBalanceSeries[];
  NonStableBalances?: IStableBalanceSeries[];
  NonStablePercents?: IStableBalanceSeries[];
  StableBalances: IStableBalanceSeries[];
  Errors: string[];
  Expanded?: boolean;
  /**
   * Used to determine when a calibration is locally generated (i.e. doesn't exist in DB or local storage)
   */
  isLocal?: boolean;
  Settings: {
    Historical: IHistoricalSettings;
    Forecast: IForecastSettings;
  };
  ChangeLogs?: ICalibrationChangelog<IStableBalanceCalibration>[];
}

export interface IStableBalanceSeries extends IBalanceSeries {
  IsHistorical: boolean;
}

export const emptyStableBalanceCalibration: IStableBalanceCalibration = {
  Adjustments: [],
  CalibrationEndDate: null,
  CalibrationId: null,
  CalibrationStartDate: null,
  Coefficients: [],
  Comment: null,
  ConstrainedRates: [],
  DateTimeCreated: null,
  DateTimeModified: null,
  FittedBalances: [],
  ForecastEndDate: null,
  ForecastStartDate: null,
  FormId: null,
  GrowthRates: [],
  LastObservationDate: null,
  NonStableBalances: [],
  NonStablePercents: [],
  ProductId: null,
  RecordStatus: null,
  SavedDate: null,
  SelectedAcquisitionIds: [],
  SelectedPolynomialForm: PolynomialForms['Poly-6th'],
  // SelectedPolynomialForm: null,
  StableBalances: [],
  TotalBalances: [],
  UnadjustedBalances: [],
  UserIdCreated: null,
  UserIdModified: null,
  VolatilityAdjustments: [],
  Errors: [],
  Settings: {
    Historical: {
      Damping: 1,
      Haircut: 0,
      StdMultiplier: STANDARD_DEVIATION_MULTIPLIER_DEFAULT,
      StdTerm: STANDARD_DEVIATION_TERM_DEFAULT,
      HistoricalUpDamper: DAMPING_UP_DEFAULT / 100,
      HistoricalDownDamper: -Math.abs(DAMPING_DOWN_DEFAULT / 100)
    },
    Forecast: {
      ForecastMethod: 0,
      ForecastGrowth: FORECAST_GROWTH_DEFAULT / 100,
      ForecastUpDamper: DAMPING_UP_DEFAULT / 100,
      ForecastDownDamper: -Math.abs(DAMPING_DOWN_DEFAULT / 100)
    }
  }
};
export enum stablebalanceCalibrationsErrors{
    historicalError =  "Negative non stable historical balances exist with current calibration settings",
    forecastError = "Negative non stable forecast balances exist with current calibration settings"
}
